// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// types
import {
	Account,
	ErrorCause,
	Feedback,
	LoadingStatus,
	Loan,
	PaymentPayload,
	Transaction,
	TransactionTransferMetadata,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, SelectChangeEvent, SxProps, Typography } from '@mui/material';

// fields
import { AmountsTransfer } from '../../fields/AmountsTransfer/AmountsTransfer';
import { AutocompleteTags } from '../../fields/AutocompleteTags/AutocompleteTags';
import { Checkbox } from '../../fields/Checkbox/Checkbox';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';
import { Empty } from '../../ui/Empty/Empty';
import { IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { ListItemTextAccount } from '../../ui/ListItemTextAccount/ListItemTextAccount';
import { TableInfoTransaction } from '../../ui/TableInfoTransaction/TableInfoTransaction';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';
import { postOrGetTags } from '../../../utils/tags';
import { translateError } from '../../../utils/translations';

// theme
import { theme } from '../../../theme';

// props
export interface ValuesProps {
	fromAccount: string;
	receiving: {
		amounts: Record<string, number>;
		total: number;
	};
	sending: {
		amounts: Record<string, number>;
		total: number;
	};
	tags: string[];
	toAccount: string;
}

export interface FormAddTransferProps {
	hasBanner?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
	setStatus: Dispatch<SetStateAction<LoadingStatus>>;
	status: LoadingStatus;
}

export const FormAddTransfer = ({
	hasBanner = false,
	onClose,
	onSuccess,
	setStatus,
	status,
}: FormAddTransferProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [accounts, setAccounts] = useState<Account[]>([]);
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [fromAccount, setFromAccount] = useState<Account | null>(null);
	const [transaction, setTransaction] = useState<Transaction | null>(null);
	const [values, setValues] = useState<ValuesProps>({
		fromAccount: '',
		receiving: {
			amounts: {},
			total: 0,
		},
		sending: {
			amounts: {},
			total: 0,
		},
		tags: [],
		toAccount: '',
	});

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// vars
	const fromAccountLoans: Loan[] = fromAccount ? (fromAccount.loans as Loan[]) : [];
	const fromAccountOwner = fromAccount?.entities?.find(
		(entity) => entity.relationship === 'OWNER'
	)?.entity;
	const toAccountSelected = accounts.find((a) => a.id === Number(values.toAccount));
	const toAccountOwner = fromAccount?.entities?.find(
		(entity) => entity.relationship === 'OWNER'
	)?.entity;
	const idForm = 'form-record-transfer';

	// check currencies
	const isSameCurrency = fromAccount?.currency === toAccountSelected?.currency;
	const isSameOwner = fromAccountOwner?.id === toAccountOwner?.id;

	// check if from account has disabled loans
	let hasDisabledLoans = false;

	for (let i = 0; i < fromAccountLoans.length; i++) {
		if (fromAccountLoans[i]?.status === 'DISABLED') {
			hasDisabledLoans = true;
			break;
		}
	}

	// fetch accounts
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&fields=currency,id,institution,name,status&orderBy=name&order=ASC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setAccounts(res.data);
		},
	});

	// options
	const optionsAccounts = accounts.map((account) => {
		return {
			disabled: account.status === 'DEACTIVATED',
			label: <ListItemTextAccount account={account} />,
			secondary: account.institution,
			value: String(account.id),
		};
	});

	const handleChangeFromAccount = async (e: SelectChangeEvent<unknown>) => {
		const url = `${process.env.REACT_APP_API_URL}/accounts/${e.target.value}?populate=entities,loans`;
		const fetchAccount = await fetch(url, {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		});

		const resAccount = await fetchAccount.json();

		if (resAccount.data) {
			setFromAccount(resAccount.data[0]);
		}

		// set fromAccount
		setValues((prevState) => {
			return {
				...prevState,
				fromAccount: String(e.target.value),
			};
		});
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;

		const valuesNew = {
			...values,
			[name]: value,
		};

		setValues(valuesNew);
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		try {
			// clear error
			setFeedback(null);

			// set loading
			setStatus('LOADING');

			// define payments using loans
			const payments: PaymentPayload[] = [];

			let total = 0;

			if (fromAccount) {
				for (let s = 0; s < fromAccount.loans.length; s++) {
					const loan = fromAccount.loans[s] as Loan;
					const amount = Number(entries[`source-sending-${s}`]);
					const amountReceived = Number(entries[`source-receiving-${s}`]) || null;

					total += amount;

					if (amount > 0) {
						if (!isSameCurrency && !amountReceived) {
							throw new Error('A receiving amount is required.', {
								cause: {
									id: 'TRANSACTION_RECEIVED_AMOUNT_NON_EXISTENT',
								},
							});
						}

						payments.push({
							amount,
							amountReceived,
							currency: fromAccount.currency,
							currencyReceived: toAccountSelected?.currency,
							loan: loan.id,
						});
					}
				}
			}

			const metadata: TransactionTransferMetadata = {
				uuid: crypto.randomUUID(),
			};

			if (fromAccount && toAccountSelected) {
				if (fromAccount.id === toAccountSelected.id) {
					throw new Error('The sending and receiving accounts must be different.', {
						cause: {
							id: 'TRANSACTION_ACCOUNTS_SAME',
						},
					});
				}
			}

			if (total === 0) {
				throw new Error('The payment amount needs to be greater than 0.', {
					cause: {
						id: 'TRANSACTION_AMOUNT_NON_EXISTENT',
					},
				});
			}

			let tags: number[] = [];

			if (entries.tags && project) {
				// post or get tags
				const resTags = await postOrGetTags({
					names: (entries.tags as string).split(','),
					project: project.id,
				});

				if (resTags.length) {
					// define tags ids
					tags = resTags.map((tag) => tag.id);
				}
			}

			const body = {
				date: formatDate({ date: entries.date, format: 'YYYY-MM-DD' }),
				fromAccount: Number(entries.fromAccount),
				metadata,
				payments,
				project: project?.id,
				status: 'COMPLETED',
				tags,
				toAccount: Number(entries.toAccount),
				type: 'TRANSFER',
			};

			const fetchAddTransfer = await fetch(`${process.env.REACT_APP_API_URL}/transactions`, {
				method: 'post',
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resAddTransfer = await fetchAddTransfer.json();

			if (resAddTransfer.error) {
				throw new Error(resAddTransfer.error.message, {
					cause: {
						id: resAddTransfer.error.id,
					},
				});
			}

			const transaction = resAddTransfer.data;

			// post comments
			if (entries.comments) {
				const fetchPostComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
					method: 'post',
					body: JSON.stringify({
						comment: entries.comments,
						transaction: transaction.id,
					}),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostComment = await fetchPostComment.json();

				if (resPostComment.error) {
					throw new Error(resPostComment.error.message, {
						cause: {
							id: resPostComment.error.id,
						},
					});
				}
			}

			// for each file
			for (let i = 0; i < files.length; i++) {
				// define form data
				const formData = new FormData();

				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				// append form data
				formData.append(
					'path',
					`bldt/projects/${project?.id}/transactions/transfers/${transaction.id}/${year}/${month}/`
				);
				formData.append('file', files[i]);
				formData.append('transaction', String(transaction.id));

				if (project) {
					formData.append('project', String(project.id));
				}

				// POST document
				let fetchPostDoc: Response;

				try {
					fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
						method: 'post',
						body: formData,
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					});
				} catch {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				if (fetchPostDoc.status === 403) {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					feedbackNew.items.push({ alert: { severity: 'error' }, response: resPostDoc });
				}
			}

			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				if (onSuccess) {
					onSuccess();
				}

				// set transaction
				setTransaction(transaction);

				if (hasBanner) {
					// set success banner
					setBanner({
						hasClose: true,
						message: t('alert.transferRecorded'),
						severity: 'success',
					});
				}

				// set status
				setStatus('SUCCESS');

				// close dialog
				if (onClose) {
					onClose();
				}
			} else {
				setFeedback(feedbackNew);
				setStatus('ERROR');
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			if (cause?.id === 'TRANSACTION_EXCEEDS_LOAN_AMOUNT') {
				cause.details.loanAmount = formatCurrency({
					currency: cause.details?.currency,
					value: cause.details?.loan?.amount,
				});

				cause.details.disbursedAmount = formatCurrency({
					currency: cause.details?.currency,
					value: cause.details?.totalDisbursed,
				});
			}

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});
			setFeedback(feedbackNew);
			setStatus('ERROR');
		}
	};

	const renderAmounts = () => {
		if (fromAccount && toAccountSelected) {
			if (fromAccount.loans.length) {
				return (
					<>
						<Grid item xs={12}>
							<AmountsTransfer
								balances={fromAccount.balances}
								idForm={idForm}
								// loans are used as a fallback when balances are not available
								loans={fromAccountLoans}
								fromAccount={fromAccount}
								onChangeReceiving={(e, total) => {
									const amountsNew: ValuesProps['receiving']['amounts'] = {
										...values.receiving.amounts,
									};
									const indexSource = e.target.getAttribute('data-index');

									amountsNew[indexSource as string] = e.target.value as unknown as number;

									setValues((prevState) => {
										return {
											...prevState,
											receiving: {
												amounts: amountsNew,
												total,
											},
										};
									});
								}}
								onChangeSending={(e, total) => {
									const amountsNew: ValuesProps['sending']['amounts'] = {
										...values.sending.amounts,
									};
									const indexSource = e.target.getAttribute('data-index');

									amountsNew[indexSource as string] = e.target.value as unknown as number;

									setValues((prevState) => {
										return {
											...prevState,
											sending: {
												amounts: amountsNew,
												total,
											},
										};
									});
								}}
								toAccount={toAccountSelected}
								title={t('sendingAccountFinancingSources')}
							/>
						</Grid>
						{hasDisabledLoans && (
							<Grid item xs={12}>
								<Alert severity="info">{t('alert.financingSourcesDisabled')}</Alert>
							</Grid>
						)}
					</>
				);
			}

			return (
				<Grid item xs={12}>
					<Alert severity="info">{t('alert.accountNoFinancingSources')}</Alert>
				</Grid>
			);
		}

		return <></>;
	};

	// loading and success states
	if (['LOADING', 'SUCCESS'].includes(status)) {
		const content = {
			children: t('empty.addTransfer.loading.onchain.content'),
		};

		const IconCircleProps: IconCircleProps = {
			size: 'xl',
			type: 'cube',
		};

		const sxBox: SxProps = {
			marginTop: theme.spacing(4),
			marginBottom: theme.spacing(4),
		};

		const title = {
			children: t('empty.addTransfer.loading.title'),
		};

		if (status === 'LOADING' && isSameOwner) {
			content.children = t('empty.addTransfer.loading.offchain.content');
		}

		if (status === 'SUCCESS') {
			content.children = t('empty.addTransfer.success.onchain.content');
			title.children = t('empty.addTransfer.success.title');
			IconCircleProps.type = 'check';
			sxBox.marginTop = 0;
			sxBox.marginBottom = 0;

			if (isSameOwner) {
				content.children = t('empty.addTransfer.success.offchain.content');
			}
		}

		return (
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Empty content={content} IconCircleProps={IconCircleProps} sxBox={sxBox} title={title} />
				</Grid>
				{status === 'SUCCESS' && (
					<>
						<Grid item xs={12}>
							<TableInfoTransaction
								fromAccount={fromAccount as Account}
								fromEntity={fromAccountOwner}
								payments={
									transaction?.payments
										? transaction.payments.map((payment) => {
												return {
													...payment,
													loan: fromAccountLoans.find((loan) => loan.id === payment.loan) || null,
												};
											})
										: []
								}
								toAccount={toAccountSelected}
								toEntity={toAccountOwner}
								total={{
									amount: values.sending.total,
									currency: fromAccount?.currency,
								}}
								totalReceived={{
									amount: values.receiving.total,
									currency: toAccountSelected?.currency,
								}}
								type="TRANSFER"
							/>
						</Grid>
						{isSameOwner && (
							<Grid item xs={12}>
								<Typography color="brand.grey.500" variant="caption">
									{t('formAddTransfer.offchain')}
								</Typography>
							</Grid>
						)}
					</>
				)}
			</Grid>
		);
	}

	return (
		<form id={idForm} name="formAddTransfer" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-date`}
						inputProps={{
							max: new Date().toISOString().split('T')[0],
						}}
						label={t('transferDate')}
						name="date"
						required={true}
						type="date"
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						helperText={
							!(values.fromAccount && values.toAccount)
								? t('formAddTransaction.payment.sendingAccount.helper')
								: undefined
						}
						id={`${idForm}-from-account`}
						label={t('sendingAccount')}
						name="fromAccount"
						onChange={handleChangeFromAccount}
						options={optionsAccounts}
						required={true}
						value={values.fromAccount}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-to-account`}
						label={t('receivingAccount')}
						name="toAccount"
						onChange={handleChangeValue}
						options={optionsAccounts}
						required={true}
						value={values.toAccount}
					/>
				</Grid>
				{!isSameCurrency && (
					<Grid item xs={12}>
						<Alert severity="info">{t('alert.currencyMismatch')}</Alert>
					</Grid>
				)}
				{renderAmounts()}
				<Grid item xs={12}>
					<FilePicker onChange={(e, filesSelected) => setFiles(filesSelected)} />
				</Grid>
				{userProjectPermissions?.comment?.create && (
					<Grid item xs={12}>
						<Input
							id={`${idForm}-comments`}
							label={t('comments')}
							minRows={2}
							multiline={true}
							name="comments"
						/>
					</Grid>
				)}
				{userProjectPermissions?.tag?.create && (
					<Grid item xs={12}>
						<AutocompleteTags
							id={`${idForm}-tags`}
							onChange={(tags) => {
								setValues((prevState) => ({
									...prevState,
									tags,
								}));
							}}
							value={values.tags}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Checkbox
						helperText={t('form.confirmation.helper')}
						id={`${idForm}-confirmation`}
						label={t('form.confirmation.label')}
						name="confirmation"
						required={true}
						sxBox={{
							padding: theme.spacing(2),
						}}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
